<template>
	<div style="padding-bottom: 6em;">
		<div class="d-flex justify-content-end align-items-center" style="position: absolute; top: 0.9em; right:3em; z-index: 1000;">
			<b-form-checkbox v-model="hideMetadataChanges" class="mr-3">Hide changes in metadata</b-form-checkbox>

			<div v-if="showSearchBar" class="search-bar">
				<input type="text" ref="searchInput" v-on:input="onSearchChange" placeholder="Search (supports /regex/)"/>
				<button @click="toggleSearchBar" class="btn btn-secondary" title="Cancel Search">
					<i class="fa fa-times"></i>
				</button>
			</div>
			<button v-else @click="toggleSearchBar" class="btn btn-compact btn-secondary mr-5" title="Search">
				<i class="fa fa-search"></i>
			</button>
		</div>

		<div class="accordion diff-accordion" role="tablist" v-for="(service, serviceIndex) in services" :key="service.id">
			
			<div class="accordion-header">
				<h4 :class="{'text-success': service.merged, 'text-muted': service.ignored}">{{ service.name }}{{ service.ignored ? ' (Ignored)' : '' }}</h4>
				<div>
					<button class="btn btn-secondary btn-compact" type="button" @click="applyToService(service.id, 'LOCAL')">Keep LOCAL</button>
					<button class="btn btn-secondary btn-compact" type="button" @click="applyToService(service.id, 'REMOTE')">Apply REMOTE</button>
					<button class="btn btn-secondary btn-compact" type="button" @click="applyToService(service.id, 'MERGE_LOCAL')">Merge into LOCAL</button>
					<button class="btn btn-secondary btn-compact" type="button" @click="applyToService(service.id, 'MERGE_REMOTE')">Merge into REMOTE</button>
					<b-button v-b-toggle="'accordion-service-' + service.id" variant="compact">
						<i class="when-open fas fa-chevron-up"></i>
						<i class="when-closed fas fa-chevron-down"></i>
					</b-button>
				</div>
			</div>
			
			<b-collapse visible :id="'accordion-service-' + service.id" :accordion="'accordion-service-' + service.id" role="tabpanel" class="accordion-body">
				
				<div v-if="service.submitError">
					<ErrorAlert :error="service.submitError" />
					<button @click="submitService(service)" class="btn btn-outline-secondary btn-sm btn-compact">Resubmit</button>
				</div>
				<div v-else-if="service.merged" class="alert alert-success" role="alert">
					<h4 class="text-success">Merged</h4>
				</div>

				<div v-if="service.loading">
					<span
						class="spinner-border spinner-border-sm mr-2"
						role="status"
						aria-hidden="true"
					></span>Loading...
				</div>

				<div v-else-if="service.error">
					<h3 v-if="service.ignored" class="text-muted">
						Errors ignored
					</h3>
					<ErrorAlert v-else :error="service.error" />
					<button v-if="!service.ignored" @click="ignoreService(service)"  class="btn btn-secondary btn-compact">Ignore</button>
					<button @click="loadLocalMetadata(service)" class="btn btn-secondary btn-compact">Retry</button>
				</div>

				<NoData v-else-if="finalState[service.id].length === 0" />

				<div v-else v-for="(folder, folderIndex) in finalState[service.id]" :key="folder.name" role="tablist" class="git-diff-folder">
					<GitDiffFolder
						:search="search"
						:folder="folder"
						:serviceIndex="serviceIndex"
						:serviceId="service.id"
						:folderIndex="folderIndex"
						:hideMetadataChanges="hideMetadataChanges"
						@selectOption="applyToFile"
						@selectOptionFiles="applyToFiles"
						@showDiffDialog="showDiffDialog"
					></GitDiffFolder>
				</div>

			</b-collapse>
			
		</div>

		<GitMergeDialog ref="gitMergeDialog" :mergeDialogSettings="mergeDialogSettings" @submit="onDiffDialogSubmit"/>

		<div class="d-flex justify-content-between align-items-center buttons-sticky-bottom">
			<button class="btn btn-secondary" type="button" @click="onBack()">Back</button>
			<div class="d-flex">
				<button class="btn btn-secondary" type="submit" @click="onSubmit()">Submit</button>
				<button class="btn btn-primary ml-3" type="submit" @click="onNext()">Submit & Run Tests</button>
			</div>
		</div>
	</div>
</template>

<script>
import GitConfig from '../../services/git-config';
import ErrorAlert from '../shared/ErrorAlert';
import GitMergeDialog from './GitMergeDialog';
import GitDiffFolder from './GitDiffFolder';
import { CHANGE_TYPE, APPLY_LOCAL_REMOTE_MAPPING } from '../../util/gitDiffConstants';
import { gitDiffFolders, convertToList, convertToTree, findFileInFolder } from '../../util/gitDiff';
import NoData from '../shared/NoData';
import debounce from '../../util/debounce';

export default {
	name: "GitDiff",
	props: [
		'remoteObjects',
		'selectedObjects',
		'uuid'
	],
	components: {
		ErrorAlert,
		GitMergeDialog,
		GitDiffFolder,
		NoData
	},
	mounted() {
		const allServices = this.$store.getters.services;
		this.services = allServices.map(({ id, name, be }) => ( { id, name, be, loading: true, ignored: false } ));

		this.services.forEach((service) => {
			this.loadLocalMetadata(service);
		});
	},
	data() {
		return {
			mergeDialogSettings: {
				left: null,
				right: null,
				final: null,
				readonly: false
			},
			services: [],
			localObjects: {},
			defaultActions: {},
			finalState: {},
			hideMetadataChanges: false,
			search: null,
			showSearchBar: false,
		};
	},
	methods: {
		onSearchChange: debounce(function(event) {
			this.search = event.target.value;
		}, 300),
		toggleSearchBar() {
			this.showSearchBar = !this.showSearchBar;
			if (this.showSearchBar) {
				this.$nextTick(() => this.$refs.searchInput.focus());
			} else {
				this.search = null;
			}
		},
		submitService(service) {
			if (service.error || service.loading || service.merged) return Promise.resolve();

			if (this.finalState[service.id].length === 0) {
				service.merged = true;
				return Promise.resolve();
			}

			service.loading = true;
			service.submitError = null;

			const config = {
				uuid: this.uuid,
				serviceId: service.id,
				folders: []
			};

			config.folders = convertToTree(this.finalState[service.id]) || [];

			return GitConfig.instance()
				.setGitConfigConfirm(config)
				.then(() => service.merged = true)
				.catch(( err ) => service.submitError = err)
				.finally(() => service.loading = false);
		},
		onSubmit() {
			const promisses = [];
			for (const service of this.services) {
				promisses.push(this.submitService(service));
			}
			return promisses;
		},
		onBack() {
			this.$emit("back");
		},
		onNext() {
			Promise.all(this.onSubmit())
				.then(() => {
					const hasError = this.services.some((service) => service.submitError || (service.error && !service.ignored));
					if (!hasError) {
						this.$emit("next");
					}
				});
		},
		onLocalObjectsLoaded(serviceId) {
			this.finalState[serviceId] = gitDiffFolders(this.localObjects[serviceId], this.remoteObjects[serviceId], this.selectedObjects[serviceId], this.defaultActions[serviceId]);
			this.refresh();
		},
		applyToFile(file, action) {
			if (file.selectedOption !== action) {
				file.content = null;
			}
			file.selectedOption = action;
		},
		applyToFiles(files, action) {
			if (files) {
				files.forEach((file) => this.applyToFile(file, APPLY_LOCAL_REMOTE_MAPPING[action][file.type]));
			}
		},
		applyToFolder(folder, action) {
			if (folder && folder.files) {
				this.applyToFiles(folder.files, action);
			}
		},
		applyToService(serviceId, action) {
			this.finalState[serviceId].forEach((folder) => this.applyToFolder(folder, action));
		},
		refresh() {
			this.finalState = Object.assign({}, this.finalState);// JSON.parse(JSON.stringify(this.finalState));
		},
		loadLocalMetadata(service) {
			service.ignored = false;
			service.loading = true;
			GitConfig.instance()
				.loadMetadata(service.be)
				.then((response) => {
					if (response.status == 200 && response.data && response.data.folders) {
						this.defaultActions[service.id] = response.data.defaultActions;
						this.localObjects[service.id] = convertToList(response.data);
						service.error = "";
						this.onLocalObjectsLoaded(service.id);
					} else {
						service.error = "Could not fetch metadata, response status: " + response.status;
					}
					service.loading = false;
				}).catch((err) => {
					service.loading = false;
					service.error = err;
				});
		},
		ignoreService(service) {
			if (confirm(`Are you sure you want to ignore ${service.name}?`)) {
				service.ignored = true;
			}
		},
		loadLocalSource(service, folder, file) {
			const localFile = findFileInFolder(this.localObjects[service.id], folder.name ,file);
			if (localFile == null) {
				return Promise.resolve(null);
			} else if (localFile.content != null) {
				return Promise.resolve(localFile.content);
			}

			return GitConfig.instance()
			.loadLocalSource(service.be, file.folderPath, file.name)
			.then((response) => {
					if (response.status == 200 && response.data) {
						localFile.content = response.data.content;
						return response.data.content;
					} else {
						throw new Error("Could not fetch file source");
					}
				});
		},
		loadRemoteSource(service, folder, file) {
			const remoteFile = findFileInFolder(this.remoteObjects[service.id], folder.name, file);
			if (remoteFile == null) {
				return Promise.resolve(null);
			} else if (remoteFile.content != null) {
				return Promise.resolve(remoteFile.content);
			}
			
			return GitConfig.instance()
				.loadRemoteSource(this.uuid, service.id, file.folderPath, file.name)
				.then((response) => {
					if (response.status == 200 && response.data) {
						remoteFile.content = response.data.content;
						return response.data.content;
					} else {
						throw new Error("Could not fetch file source");
					}
				});
			},
		showDiffDialog(serviceId, folderIndex, file) {
			const service = this.services.find((s) => s.id === serviceId);
			const folder = this.finalState[serviceId][folderIndex];
			Promise.all([
				this.loadLocalSource(service, folder, file),
				this.loadRemoteSource(service, folder, file)
			]).then(([localSource, remoteSource]) => {

				const settings = {
					readonly: file.selectedOption !== 'MERGE_LOCAL' && file.selectedOption !== 'MERGE_REMOTE',
					serviceId: service.id,
					folderName: folder.name,
					fileName: file.name,
					filePath: file.path
				};

				if (file.type === CHANGE_TYPE.DELETED || file.type === CHANGE_TYPE.NEW) {
					settings.left = null;
					settings.final = localSource;
					settings.right = remoteSource;
				} else {
					settings.left = localSource;
					settings.right = remoteSource;
					switch (file.selectedOption) {
						case 'MERGE_LOCAL':
							settings.final = file.content != null ? file.content : localSource;
							break;
						case 'MERGE_REMOTE':
							settings.final = file.content != null ? file.content : remoteSource;
							break;
						case 'LOCAL':
						case 'REMOTE':					
							settings.final = localSource;
							break;
						default:
							break;
					}
				}
				
				this.mergeDialogSettings = settings;
				this.$refs.gitMergeDialog.open();
			});
		},
		getFinalStateFile(serviceId, folderName, fileName, filePath) {
			const folder = this.finalState[serviceId] &&
				this.finalState[serviceId].find(({ name }) => name === folderName) || null;
			const file = folder && 
						folder.files && 
						folder.files.find(({ name, path }) => name === fileName && path === filePath) || null;
			return file;
		},
		onDiffDialogSubmit(newData) {
			if (this.mergeDialogSettings.fileName) {
				const file = this.getFinalStateFile(
					this.mergeDialogSettings.serviceId, 
					this.mergeDialogSettings.folderName, 
					this.mergeDialogSettings.fileName,
					this.mergeDialogSettings.filePath
				);

				file.content = newData;
			}

		}
	}
};
</script>

<style>
	
	.collapsed > .when-open,
	.not-collapsed > .when-closed {
		display: none;
	}

	.buttons-sticky-bottom {
		position: fixed;
		left: 80px;
		bottom: 0;
		width: calc(100% - 90px);
		margin: 0;
		padding: 1.5em 2.1em;
		z-index: 1024;
		background-color: #FFF;
		box-shadow: 0 0 1em 0 rgb(0,0,0,0.1);
	}

	.search-bar {
		position: fixed;
		z-index: 3000;
		right: 270px;
		display: flex;
		gap: 0.5em;
		background-color: #fafafa;
		padding: 2px;
		margin: -2px;
		border: 1px solid #fafafa;
		border-bottom-left-radius: 0.5rem;
	}

	.header-sticky-top .btn {
		margin-left: 1em;
	}

	.accordion {
		border: 1px solid #ccc;
		overflow: hidden;
	}

	.accordion + .accordion {
		border-top-width: 0;
	}

	.accordion-header {
		padding: 0.6em 1.1em;
		display: flex;
		justify-content: space-between;
		align-items: center;
		background-color: #F8F8F8;
	}

	.accordion-header :is(h2, h3, h4, h5) {
		margin: 0;
	}

	.accordion-header div {
		display: flex;
		gap: 1em;
	}

	.accordion-body {
		padding: 1em 1.1em;
	}

	.accordion-body.show {
		border-top: 1px solid #ccc;
	}

	.accordion-body .alert ~ .btn {
		display: inline-block;
		margin-right: 1em;
	}

	.git-diff-folder + .git-diff-folder {
		margin-top: 1em;
	}

	.accordion-body table.table:only-child {
    	margin: 0;
	}

	table label {
		margin: 0;
	}

</style>